// File: src/components/pages/Publish.jsx
// Publish tab - Compare templates and display differences (MINIMAL CHANGES - CONFLICT VALIDATION ADDED)

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronLeft, ChevronRight, Clock, Coffee, Upload, AlertCircle, AlertTriangle, Sparkles, UserX } from 'lucide-react';
import { bookingTemplateAPI, bookingSlotAPI } from '../../services/api';
import { getMondayOfCurrentWeek } from '../../utils/DateUtils';
import { AUTH0_NAMESPACE, DAYS_OF_WEEK } from '../../utils/constants';
import '../../styles/unified.css';

export default function Publish() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');

  const [activeTemplate, setActiveTemplate] = useState(null);
  const [publishedTemplate, setPublishedTemplate] = useState(null);
  const [templateDifferences, setTemplateDifferences] = useState(null);
  const [bookingConflicts, setBookingConflicts] = useState(null); // NEW
  const [exceptionalChanges, setExceptionalChanges] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMondayOfCurrentWeek());
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false); // NEW
  const [isPublishing, setIsPublishing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (isServiceProvider) {
      loadTemplates();
    }
  }, [isServiceProvider]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      // Load active template
      const activeResponse = await bookingTemplateAPI.getUserTemplates(getAccessTokenSilently);
      if (activeResponse.success) {
        const active = activeResponse.data.find(t => t.isActive);
        if (active) {
          setActiveTemplate(active);
          
          // Load published template
          try {
            const publishedResponse = await bookingSlotAPI.getCurrentPublishedTemplate(getAccessTokenSilently);
            if (publishedResponse.success && publishedResponse.data) {
              setPublishedTemplate(publishedResponse.data);
              
              // Compare templates
              const differences = compareTemplates(active, publishedResponse.data);
              setTemplateDifferences(differences);

              // Compare exceptional slots independently
              const excChanges = compareExceptionalSlots(
                active.exceptionalSlots || [],
                publishedResponse.data.exceptionalSlots || []
              );
              setExceptionalChanges(excChanges);

              // NEW: Validate bookings if there are changes
              if (differences.hasChanges || excChanges.hasChanges) {
                await validateBookings(active.templateId);
              }
            } else {
              // No published template exists yet
              setPublishedTemplate(null);
              setTemplateDifferences({ hasChanges: true, isFirstPublish: true });
              setBookingConflicts(null); // NEW
              setExceptionalChanges(compareExceptionalSlots(active.exceptionalSlots || [], []));
            }
          } catch (publishedError) {
            console.log('No published template found (first publish):', publishedError);
            setPublishedTemplate(null);
            setTemplateDifferences({ hasChanges: true, isFirstPublish: true });
            setBookingConflicts(null); // NEW
            setExceptionalChanges(compareExceptionalSlots(active.exceptionalSlots || [], []));
          }
        } else {
          showMessage('No active template found. Please activate a template first.', 'error');
        }
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      showMessage('Failed to load templates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Validate bookings function
  const validateBookings = async (templateId) => {
    setIsValidating(true);
    try {
      const validationResult = await bookingSlotAPI.validatePublish(templateId, getAccessTokenSilently);
      
      if (validationResult.success) {
        setBookingConflicts(validationResult);
      } else {
        console.error('Validation failed:', validationResult);
        showMessage('Failed to validate bookings', 'error');
      }
    } catch (error) {
      console.error('Error validating bookings:', error);
      showMessage('Failed to validate bookings', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  // ── Exceptional slot helpers (read-only display) ───────────────────────────
  const excTimeToMin = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const countWindowSlots = (windows) => (windows || []).length;

  const fmtExcDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const fmtTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const p = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${p}`;
  };

  const compareExceptionalSlots = (activeSlots, publishedSlots) => {
    const pubMap = new Map((publishedSlots || []).map(es => [es.date, es]));
    const actMap = new Map((activeSlots  || []).map(es => [es.date, es]));

    const added = [], removed = [], modified = [], unchanged = [];

    actMap.forEach((es, date) => {
      if (!pubMap.has(date)) {
        added.push({ date, activeWindows: es.windows, publishedWindows: [] });
      } else {
        const pub = pubMap.get(date);
        const winsA = es.windows || [];
        const winsB = pub.windows || [];
        const differ = winsA.length !== winsB.length ||
          winsA.some((w, i) => !winsB[i] ||
            w.startTime !== winsB[i].startTime || w.endTime !== winsB[i].endTime);
        if (differ) {
          modified.push({ date, activeWindows: es.windows, publishedWindows: pub.windows || [] });
        } else {
          unchanged.push({ date, activeWindows: es.windows });
        }
      }
    });

    pubMap.forEach((es, date) => {
      if (!actMap.has(date)) {
        removed.push({ date, publishedWindows: es.windows });
      }
    });

    const hasChanges = added.length > 0 || removed.length > 0 || modified.length > 0;
    return { added, removed, modified, unchanged, hasChanges };
  };

  const compareTemplates = (active, published) => {
    if (!published) {
      return { hasChanges: true, isFirstPublish: true, changes: [] };
    }

    const changes = [];
    
    // Compare settings
    const settingsChanges = compareSettings(active.settings, published.settings);
    if (settingsChanges.length > 0) {
      changes.push(...settingsChanges);
    }

    // Compare private appointments
    const appointmentChanges = comparePrivateAppointments(
      active.privateAppointments || [],
      published.privateAppointments || []
    );
    if (appointmentChanges.length > 0) {
      changes.push(...appointmentChanges);
    }

    return {
      hasChanges: changes.length > 0,
      isFirstPublish: false,
      changes: changes,
      changedDates: getChangedDates(active, published)
    };
  };

  const compareSettings = (activeSetting, publishedSettings) => {
    const changes = [];
    const keysToCompare = [
      'dateFrom', 'dateTo', 'dayFrom', 'dayTo',
      'startTime', 'endTime', 'slotDuration', 'breakDuration',
      'daysInAdvance', 'myBreakFrom', 'myBreakTo'
    ];

    keysToCompare.forEach(key => {
      if (activeSetting[key] !== publishedSettings[key]) {
        changes.push({
          type: 'setting',
          field: key,
          oldValue: publishedSettings[key],
          newValue: activeSetting[key]
        });
      }
    });

    return changes;
  };

  const comparePrivateAppointments = (activeAppts, publishedAppts) => {
    const changes = [];
    
    // Create maps for comparison
    const activeMap = new Map(activeAppts.map(apt => [`${apt.date}-${apt.timeFrom}-${apt.timeTo}`, apt]));
    const publishedMap = new Map(publishedAppts.map(apt => [`${apt.date}-${apt.timeFrom}-${apt.timeTo}`, apt]));

    // Find added appointments
    activeMap.forEach((apt, key) => {
      if (!publishedMap.has(key)) {
        changes.push({
          type: 'appointment',
          action: 'added',
          date: apt.date,
          timeFrom: apt.timeFrom,
          timeTo: apt.timeTo
        });
      }
    });

    // Find removed appointments
    publishedMap.forEach((apt, key) => {
      if (!activeMap.has(key)) {
        changes.push({
          type: 'appointment',
          action: 'removed',
          date: apt.date,
          timeFrom: apt.timeFrom,
          timeTo: apt.timeTo
        });
      }
    });

    return changes;
  };

  const getChangedDates = (active, published) => {
    const changedDates = new Set();
    
    // Collect all dates with appointment changes
    const activeAppts = active.privateAppointments || [];
    const publishedAppts = published.privateAppointments || [];
    
    activeAppts.forEach(apt => changedDates.add(apt.date));
    publishedAppts.forEach(apt => changedDates.add(apt.date));

    return Array.from(changedDates);
  };

    const isSlotChanged = (date, slotStart, slotEnd) => {
    // Guard: first publish has no 'changes' array — nothing to compare
    if (!templateDifferences || !templateDifferences.hasChanges || templateDifferences.isFirstPublish) {
      return false;
    }
    // Guard: changes may be undefined if templateDifferences was set without it
    if (!Array.isArray(templateDifferences.changes) || templateDifferences.changes.length === 0) {
      return false;
    }

    const d = date;
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    
    // Check if this date has appointment changes
    return templateDifferences.changes.some(change => {
      if (change.type === 'appointment' && change.date === dateStr) {
        const changeStartMin = timeToMinutes(change.timeFrom);
        const changeEndMin = timeToMinutes(change.timeTo);
        const slotStartMin = timeToMinutes(slotStart);
        const slotEndMin = timeToMinutes(slotEnd);
        
        // Check if slot overlaps with changed appointment
        return !(slotEndMin <= changeStartMin || slotStartMin >= changeEndMin);
      }
      return false;
    });
  };

  const handlePublish = async () => {
    if (!activeTemplate) {
      showMessage('No active template to publish', 'error');
      return;
    }

    if (!templateDifferences?.hasChanges && !exceptionalChanges?.hasChanges) {
      showMessage('No changes to publish', 'warning');
      return;
    }

    // NEW: Block publishing if there are booking conflicts
    if (bookingConflicts && bookingConflicts.hasConflicts) {
      showMessage(
        `Cannot publish: ${bookingConflicts.totalConflicts} booking conflict(s) detected. Please cancel conflicting bookings first.`,
        'error'
      );
      return;
    }

    const regularCount     = templateDifferences?.changes?.length || 0;
    const excAddedCount    = exceptionalChanges?.added?.length    || 0;
    const excRemovedCount  = exceptionalChanges?.removed?.length  || 0;
    const excModifiedCount = exceptionalChanges?.modified?.length || 0;
    const excTotalChanged  = excAddedCount + excRemovedCount + excModifiedCount;
    const totalChanges     = regularCount + excTotalChanged;

    const excSummaryParts = [
      excAddedCount    > 0 && `${excAddedCount} exceptional slot${excAddedCount    !== 1 ? 's' : ''} added`,
      excModifiedCount > 0 && `${excModifiedCount} exceptional slot${excModifiedCount !== 1 ? 's' : ''} modified`,
      excRemovedCount  > 0 && `${excRemovedCount} exceptional slot${excRemovedCount  !== 1 ? 's' : ''} removed`,
    ].filter(Boolean).join(', ');

    const confirmMessage = templateDifferences.isFirstPublish
      ? `This will be your first published template${excTotalChanged > 0 ? ` (includes ${excTotalChanged} exceptional slot${excTotalChanged !== 1 ? 's' : ''})` : ''}. Students will be able to see and book slots. Continue?`
      : `${totalChanges} change${totalChanges !== 1 ? 's' : ''} detected${excSummaryParts ? ` (${excSummaryParts})` : ''}. Publish updated template?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsPublishing(true);
    try {
      const response = await bookingSlotAPI.publishTemplate(activeTemplate.templateId, getAccessTokenSilently);
      if (response.success) {
        showMessage(`Template published successfully! Version ${response.data.version} created.`, 'success');
        // Reload templates to get latest published version
        await loadTemplates();
      }
    } catch (error) {
      console.error('Failed to publish template:', error);
      showMessage(error.message || 'Failed to publish template', 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  // Helper functions
  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const isDateInRange = (date) => {
    if (!activeTemplate) return false;
    const checkDate = new Date(date);
    const from = new Date(activeTemplate.settings.dateFrom + 'T00:00:00');
    const to   = new Date(activeTemplate.settings.dateTo   + 'T00:00:00');
    return checkDate >= from && checkDate <= to;
  };

  const isDayEnabled = (date) => {
    if (!activeTemplate) return false;
    const dayName = DAYS_OF_WEEK[date.getDay()];
    const fromIndex = DAYS_OF_WEEK.indexOf(activeTemplate.settings.dayFrom);
    const toIndex = DAYS_OF_WEEK.indexOf(activeTemplate.settings.dayTo);
    const dayIndex = DAYS_OF_WEEK.indexOf(dayName);
    
    if (fromIndex <= toIndex) {
      return dayIndex >= fromIndex && dayIndex <= toIndex;
    } else {
      return dayIndex >= fromIndex || dayIndex <= toIndex;
    }
  };

  const isDateBookable = (date) => {
    if (!activeTemplate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const advanceDate = new Date(today);
    advanceDate.setDate(today.getDate() + activeTemplate.settings.daysInAdvance);
    return checkDate >= today && checkDate <= advanceDate;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isSlotBlocked = (date, slotStart, slotEnd) => {
    if (!activeTemplate) return { blocked: false };
    const slotStartMin = timeToMinutes(slotStart);
    const slotEndMin = timeToMinutes(slotEnd);
    
    const d2 = date;
    const dateStr = `${d2.getFullYear()}-${String(d2.getMonth()+1).padStart(2,'0')}-${String(d2.getDate()).padStart(2,'0')}`;
    const privateAppointments = activeTemplate.privateAppointments || [];
    
    for (const apt of privateAppointments) {
      if (apt.date === dateStr) {
        const aptStartMin = timeToMinutes(apt.timeFrom);
        const aptEndMin = timeToMinutes(apt.timeTo);
        if (!(slotEndMin <= aptStartMin || slotStartMin >= aptEndMin)) {
          return { blocked: true, reason: 'Private' };
        }
      }
    }
    
    return { blocked: false };
  };

  const getDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getExceptionalEntry = (date) => {
    const dateStr = getDateStr(date);
    return (activeTemplate?.exceptionalSlots || []).find(es => es.date === dateStr) || null;
  };

  const generateTimeSlots = () => {
    if (!activeTemplate) return [];
    
    const slots = [];
    const settings = activeTemplate.settings;
    const [startHour, startMin] = settings.startTime.split(':').map(Number);
    const [endHour, endMin] = settings.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const slotDuration = settings.slotDuration;
    const breakDuration = settings.breakDuration;
    
    const hasMyBreak = settings.myBreakFrom && settings.myBreakTo;
    const myBreakStartMin = hasMyBreak ? timeToMinutes(settings.myBreakFrom) : null;
    const myBreakEndMin = hasMyBreak ? timeToMinutes(settings.myBreakTo) : null;
    
    let currentMinutes = startMinutes;
    let breakInserted = false;
    
    while (currentMinutes + slotDuration <= endMinutes) {
      if (hasMyBreak && !breakInserted && currentMinutes + slotDuration > myBreakStartMin) {
        if (currentMinutes < myBreakStartMin) {
          const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
          const slotEnd = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
          
          if (myBreakStartMin - currentMinutes >= slotDuration) {
            slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
          }
        }
        
        const breakStart = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
        const breakEnd = `${Math.floor(myBreakEndMin / 60).toString().padStart(2, '0')}:${(myBreakEndMin % 60).toString().padStart(2, '0')}`;
        slots.push({ start: breakStart, end: breakEnd, type: 'break' });
        
        currentMinutes = myBreakEndMin;
        breakInserted = true;
        continue;
      }
      
      const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
      const slotEndMinutes = currentMinutes + slotDuration;
      const slotEnd = `${Math.floor(slotEndMinutes / 60).toString().padStart(2, '0')}:${(slotEndMinutes % 60).toString().padStart(2, '0')}`;
      
      slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
      currentMinutes = slotEndMinutes + breakDuration;
    }
    
    return slots;
  };

  const formatTime12hr = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const weekDates = getWeekDates(currentWeekStart);
  const timeSlots = generateTimeSlots();

  if (!isServiceProvider) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>Only service providers can publish templates</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="tab-content-placeholder">
        <h2>Loading...</h2>
        <p>Comparing templates...</p>
      </div>
    );
  }

  if (!activeTemplate) {
    return (
      <div className="tab-content-placeholder">
        <h2>No Active Template</h2>
        <p>Please activate a template in the Templates tab before publishing</p>
      </div>
    );
  }

  const hasChanges = (templateDifferences?.hasChanges || exceptionalChanges?.hasChanges) || false;
  const isFirstPublish = templateDifferences?.isFirstPublish || false;

  return (
    <div className="publish-container">
      {/* Alert Messages */}
      {message && (
        <div className={`save-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Publish Header */}
      <div className="publish-header">
        <div className="publish-header-left">
          <span className="badge-active">ACTIVE</span>
          <div className="publish-template-name">
            {activeTemplate.templateName}
            {hasChanges && (
              <span className="badge-changed">Changed</span>
            )}
          </div>
        </div>
        <button 
          onClick={handlePublish} 
          className="btn-publish"
          disabled={isPublishing || !hasChanges || (bookingConflicts && bookingConflicts.hasConflicts)} 
        >
          <Upload size={20} />
          {isPublishing ? 'Publishing...' : hasChanges ? 'PUBLISH' : 'PUBLISHED'}
        </button>
      </div>

      {/* No Changes Message */}
      {!hasChanges && !isFirstPublish && (
        <div className="no-changes-alert">
          <AlertCircle size={20} />
          <span>Nothing to publish - template is already up to date</span>
        </div>
      )}

      {/* NEW: Validation Loading State */}
      {isValidating && (
        <div className="validation-loading">
          <div className="spinner"></div>
          <span>Validating existing bookings...</span>
        </div>
      )}

      {/* NEW: Booking Conflicts Warning */}
      {bookingConflicts && bookingConflicts.hasConflicts && (
        <div className="conflicts-alert">
          <AlertTriangle size={20} />
          <div className="conflicts-content">
            <h3>⚠️ {bookingConflicts.totalConflicts} Booking Conflict(s) Detected</h3>
            <p>The following bookings must be cancelled before you can publish these changes:</p>
            
            <div className="conflicts-list">
              {bookingConflicts.conflicts.map((conflict, idx) => (
                <div key={idx} className="conflict-item">
                  <div className="conflict-header">
                    <strong>{conflict.studentName}</strong>
                    <span className="conflict-date">
                      {conflict.bookingDate} • {formatTime12hr(conflict.startTime)} – {formatTime12hr(conflict.endTime)}
                    </span>
                  </div>
                  <ul className="conflict-reasons">
                    {conflict.reasons.map((reason, ridx) => (
                      <li key={ridx} className={`reason-${reason.type}`}>
                        {reason.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="conflicts-footer">
              Please go to <strong>Manage Bookings</strong> to cancel these bookings, 
              then return here to publish your changes.
            </p>
          </div>
        </div>
      )}


      {/* Exceptional Slots — full change summary (added / removed / modified / unchanged) */}
      {(() => {
        // Build a unified list across all four buckets
        const allDates = [
          ...(exceptionalChanges?.added     || []).map(e => ({ ...e, status: 'added',     windows: e.activeWindows })),
          ...(exceptionalChanges?.removed   || []).map(e => ({ ...e, status: 'removed',   windows: e.publishedWindows })),
          ...(exceptionalChanges?.modified  || []).map(e => ({ ...e, status: 'modified',  windows: e.activeWindows })),
          ...(exceptionalChanges?.unchanged || []).map(e => ({ ...e, status: 'unchanged', windows: e.activeWindows })),
        ].sort((a, b) => a.date.localeCompare(b.date));

        if (allDates.length === 0) return null;

        const statusMeta = {
          added:     { label: 'ADDED',     badgeClass: 'exc-badge-added'     },
          removed:   { label: 'REMOVED',   badgeClass: 'exc-badge-removed'   },
          modified:  { label: 'MODIFIED',  badgeClass: 'exc-badge-modified'  },
          unchanged: { label: 'NO CHANGE', badgeClass: 'exc-badge-unchanged' },
        };

        return (
          <div className="exc-summary-section">
            <div className="exc-summary-header">
              <Sparkles size={15} className="exc-summary-icon" />
              <span className="exc-summary-title">Exceptional Slots</span>
              <span className="exc-summary-count">
                {allDates.length} date{allDates.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="exc-summary-rows">
              {allDates.map((es) => {
                const totalSlots = countWindowSlots(es.windows);
                const meta = statusMeta[es.status];
                const hasAddConflict = bookingConflicts?.conflicts?.some(
                  c => c.bookingDate === es.date &&
                       c.reasons?.some(r => r.type === 'exceptional_slot')
                );
                const hasRemoveConflict = bookingConflicts?.conflicts?.some(
                  c => c.bookingDate === es.date &&
                       c.reasons?.some(r => r.type === 'exceptional_slot_removed')
                );
                const hasConflict = hasAddConflict || hasRemoveConflict;

                return (
                  <div key={es.date} className={`exc-summary-row ${hasConflict ? 'exc-row-conflict' : ''}`}>
                    <span className="exc-row-date">
                      {hasConflict && <AlertTriangle size={12} className="exc-row-warn" />}
                      {fmtExcDate(es.date)}
                    </span>
                    <span className="exc-row-windows">
                      {(es.windows || [])
                        .slice()
                        .sort((a, b) => a.startTime.localeCompare(b.startTime))
                        .map((w, i) => (
                          <span key={i} className={`exc-row-win ${es.status === 'removed' ? 'exc-win-removed' : ''}`}>
                            {formatTime12hr(w.startTime)}–{formatTime12hr(w.endTime)}
                          </span>
                        ))}
                    </span>
                    <div className="exc-row-right">
                      <span className="exc-row-slots">
                        {totalSlots} slot{totalSlots !== 1 ? 's' : ''}
                      </span>
                      <span className={`exc-status-badge ${meta.badgeClass}`}>
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Private Appointments — changes summary */}
      {hasChanges && !isFirstPublish && Array.isArray(templateDifferences?.changes) && (() => {
        const aptChanges = templateDifferences.changes
          .filter(c => c.type === 'appointment')
          .sort((a, b) => a.date.localeCompare(b.date) || a.timeFrom.localeCompare(b.timeFrom));
        if (aptChanges.length === 0) return null;
        return (
          <div className="exc-summary-section pa-summary-section">
            <div className="exc-summary-header">
              <UserX size={15} className="pa-summary-icon" />
              <span className="exc-summary-title">Private Appointment Changes</span>
              <span className="exc-summary-count pa-summary-count">
                {aptChanges.length} change{aptChanges.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="exc-summary-rows">
              {aptChanges.map((change, idx) => (
                <div key={idx} className="exc-summary-row">
                  <span className="exc-row-date">{fmtExcDate(change.date)}</span>
                  <span className="exc-row-windows">
                    <span className="exc-row-win pa-win-time">
                      {formatTime12hr(change.timeFrom)} – {formatTime12hr(change.timeTo)}
                    </span>
                  </span>
                  <span className={`exc-status-badge ${change.action === 'added' ? 'exc-badge-added' : 'exc-badge-removed'}`}>
                    {change.action === 'added' ? 'ADDED' : 'REMOVED'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Changes Summary — Settings only */}
      {hasChanges && !isFirstPublish && Array.isArray(templateDifferences?.changes) && (() => {
        const settingChanges = templateDifferences.changes.filter(c => c.type === 'setting');
        if (settingChanges.length === 0) return null;
        return (
          <div className="changes-summary">
            <div className="changes-summary-header">
              <AlertCircle size={20} />
              <span>{settingChanges.length} template setting change{settingChanges.length !== 1 ? 's' : ''} detected</span>
            </div>
            <div className="changes-list">
              {settingChanges.slice(0, 5).map((change, idx) => (
                <div key={idx} className="change-item">
                  <span>
                    <strong>{change.field}:</strong> {change.oldValue} → {change.newValue}
                  </span>
                </div>
              ))}
              {settingChanges.length > 5 && (
                <div className="change-item-more">
                  +{settingChanges.length - 5} more changes...
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Preview Card */}
      <div className="preview-card">
        <div className="preview-header">
          <h2 className="preview-title">Weekly Preview {hasChanges && <span className="preview-modified">(Modified)</span>}</h2>
          <div className="week-navigation">
            <button onClick={() => navigateWeek(-1)} className="nav-button">
              <ChevronLeft size={24} />
            </button>
            <span className="week-label">
              {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {weekDates[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <button onClick={() => navigateWeek(1)} className="nav-button">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-wrapper">
            <div className="grid-header">
              <div className="time-header">Time</div>
              {weekDates.map((date, idx) => {
                const excEntry    = getExceptionalEntry(date);
                const isExc       = !!excEntry;
                const isInRange   = isDateInRange(date);
                const isDayActive = isDayEnabled(date);
                const isBookable  = isDateBookable(date);
                const slotCount = isExc ? (excEntry.windows || []).length : 0;

                if (isExc) {
                  return (
                    <div key={idx} className="day-header day-header-exceptional">
                      <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                      <div className="day-date day-date-exceptional">{date.getDate()}</div>
                      <div className="day-status day-status-exceptional">
                        <Sparkles size={10} /> Exc. ({slotCount})
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className={`day-header ${isInRange && isDayActive ? 'day-header-active' : 'day-header-inactive'}`}>
                    <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                    <div className={`day-date ${isInRange && isDayActive ? 'day-date-active' : 'day-date-inactive'}`}>{date.getDate()}</div>
                    {isInRange && isDayActive && (
                      <div className={`day-status ${isBookable ? 'day-status-open' : 'day-status-future'}`}>
                        {isBookable ? '✓ Open' : 'Future'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="slots-container">
              {timeSlots.map((slot, slotIdx) => (
                <div key={slotIdx} className={`slot-row ${slot.type === 'break' ? 'break-row' : ''}`}>
                  <div className="time-cell">
                    <Coffee size={14} className={slot.type === 'break' ? 'break-icon' : 'time-icon'} />
                    <div className="time-range">
                      <div className="time-start">{formatTime12hr(slot.start)}</div>
                      <div className="time-end">{formatTime12hr(slot.end)}</div>
                    </div>
                  </div>
                  {slot.type === 'break' ? (
                    <div className="break-cell-full" style={{ gridColumn: '2 / -1' }}>
                      <div className="break-label">
                        <Coffee size={16} />
                        <span>BREAK TIME</span>
                      </div>
                    </div>
                  ) : (
                    weekDates.map((date, dateIdx) => {
                      const excEntry    = getExceptionalEntry(date);
                      const isExc       = !!excEntry;
                      const isInRange   = isDateInRange(date);
                      const isDayActive = isDayEnabled(date);
                      const isBookable  = isDateBookable(date);
                      const isAvailable = isInRange && isDayActive;
                      const blockCheck  = isSlotBlocked(date, slot.start, slot.end);
                      const isChanged   = isSlotChanged(date, slot.start, slot.end);

                      // Exceptional date — all cells display as exceptional
                      if (isExc) {
                        return (
                          <div key={dateIdx} className="slot-cell slot-exceptional">
                            <div className="slot-status">
                              <div className="status-exceptional"><Sparkles size={10} /> Exc.</div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={dateIdx}
                          className={`slot-cell ${!isAvailable ? 'slot-unavailable' : blockCheck.blocked ? 'slot-blocked' : isBookable ? 'slot-available' : 'slot-locked'} ${isChanged ? 'slot-changed' : ''}`}
                        >
                          <div className="slot-status">
                            {!isAvailable ? (
                              <div className="status-unavailable">-</div>
                            ) : blockCheck.blocked ? (
                              <>
                                <div className="status-blocked">{blockCheck.reason}</div>
                                {isChanged && <div className="status-change-indicator">●</div>}
                              </>
                            ) : isBookable ? (
                              <div className="status-available">Available</div>
                            ) : (
                              <div className="status-locked">Locked</div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>

            {timeSlots.length === 0 && (
              <div className="empty-state">
                <Clock size={48} className="empty-icon" />
                <p className="empty-text">No time slots available</p>
                <p className="empty-subtext">Check template configuration</p>
              </div>
            )}
          </div>
        </div>

        <div className="legend">
          <div className="legend-item">
            <div className="legend-box legend-box-available"></div>
            <span className="legend-text">Available to book</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-blocked"></div>
            <span className="legend-text">Blocked (Break/Private)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-changed"></div>
            <span className="legend-text">Modified (unpublished changes)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-locked"></div>
            <span className="legend-text">Future (not yet bookable)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-unavailable"></div>
            <span className="legend-text">Outside range/unavailable</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-exceptional"></div>
            <span className="legend-text">Exceptional slot</span>
          </div>
        </div>
      </div>
    </div>
  );
}